import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import FavoriteIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteFilledIcon from '@mui/icons-material/Favorite';
import LocalOfferIcon from '@mui/icons-material/LocalOfferOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import OfferMeta from '../OfferCard/components/OfferMeta';
import useFavoriteOffer from '../useFavoriteOffer';
import { ButtonContainer, StyledButton } from '../OfferActions/styles';

const formatPhone = (input = '') => {
  const digits = input.replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  let formatted = '';
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};

const isPhoneFormatted = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

const formatPhoneIfNeeded = (phone) => {
  if (!phone) return phone;
  if (isPhoneFormatted(phone)) return phone;
  return formatPhone(phone);
};

const InfoPopup = ({
  active,
  address,
  businessImageUrl,
  businessName,
  claimsRemaining,
  closePopup,
  description,
  termsAndConditions,
  distance,
  endDate,
  headline1,
  headline2,
  id,
  offerLocation,
  openClaim,
  phone,
  revalidateList,
  showAddress,
  showPhone,
  allowUnavailableActions = false,
}) => {
  const { isFavorite, isUpdating, toggleFavorite } = useFavoriteOffer(id, { revalidateList });
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [showMapsChoice, setShowMapsChoice] = useState(false);
  const secondaryActionDisabled = !active && !allowUnavailableActions;

  const getResolvedAddress = () => {
    if (address) return address;
    if (offerLocation?.local && offerLocation.local.length > 0) {
      const addressWithToggle = offerLocation.local.find((addr) => addr.showAddress === true);
      if (addressWithToggle) return addressWithToggle.address;
      return offerLocation.local[0].address;
    }
    return null;
  };

  const resolvedAddress = getResolvedAddress();
  const resolvedShowAddress = showAddress === true;

  const handleOpenGoogleMaps = () => {
    if (!resolvedAddress) return;
    const encodedAddress = encodeURIComponent(resolvedAddress);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    setShowMapsChoice(false);
    setShowLocationPopup(false);
  };

  const handleOpenAppleMaps = () => {
    if (!resolvedAddress) return;
    const encodedAddress = encodeURIComponent(resolvedAddress);
    window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
    setShowMapsChoice(false);
    setShowLocationPopup(false);
  };

  const handleDirectionsClick = () => {
    if (!resolvedAddress) return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      setShowMapsChoice(true);
    } else {
      handleOpenGoogleMaps();
    }
  };

  const handleClaimClick = () => {
    closePopup();
    openClaim();
  };

  return (
    <>
      <Dialog onClose={closePopup} open fullWidth maxWidth="sm">
        <Box sx={{ position: 'relative', px: 1, pt: 5, pb: 0 }}>
          <IconButton
            aria-label="close"
            onClick={closePopup}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'black',
            }}
          >
            <CloseIcon />
          </IconButton>
          <OfferMeta quantity={claimsRemaining} endDate={endDate} />
        </Box>
        <DialogTitle>
          <Box height={30}>
            <img src={businessImageUrl} height={30} alt="" />
          </Box>
          <Typography variant="h5">
            {headline1} | {headline2}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6">Terms</Typography>
          <Typography variant="body2">
            {termsAndConditions || 'No terms defined'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0, '& > *': { margin: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', width: '100%', gap: 0 }}>
            {showPhone === true && (
              <Tooltip title="Call business" enterDelay={500} leaveDelay={50}>
                <span style={{ margin: 0, display: 'inline-flex' }}>
                  <IconButton
                    aria-label="call business"
                    disabled={secondaryActionDisabled}
                    onClick={() => setShowPhonePopup(true)}
                    size="large"
                    sx={{ padding: '6px' }}
                  >
                    <PhoneIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {resolvedShowAddress === true && (
              <Tooltip title="Directions" enterDelay={500} leaveDelay={50}>
                <span style={{ margin: 0, display: 'inline-flex' }}>
                  <IconButton
                    aria-label="Directions"
                    disabled={secondaryActionDisabled}
                    onClick={() => setShowLocationPopup(true)}
                    size="large"
                    sx={{ padding: '6px' }}
                  >
                    <LocationOnIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Visit website" enterDelay={500} leaveDelay={50}>
              <span style={{ margin: 0, display: 'inline-flex' }}>
                <IconButton
                  aria-label="go to website"
                  disabled={secondaryActionDisabled}
                  href={`/api/common/offer_homepage/${id}`}
                  target="_blank"
                  rel="nofollow"
                  size="large"
                  sx={{ padding: '6px' }}
                >
                  <HomeIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              enterDelay={500}
              leaveDelay={50}
            >
              <span style={{ margin: 0, display: 'inline-flex' }}>
                <IconButton
                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  onClick={toggleFavorite}
                  disabled={isUpdating}
                  size="large"
                  sx={{ padding: '6px' }}
                >
                  {isFavorite ? (
                    <FavoriteFilledIcon color="error" />
                  ) : (
                    <FavoriteIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <ButtonContainer>
              <Tooltip title="Claim offer" enterDelay={500} leaveDelay={50}>
                <span>
                  <StyledButton
                    color="primary"
                    disableElevation
                    variant="contained"
                    startIcon={<LocalOfferIcon />}
                    disabled={!active}
                    onClick={handleClaimClick}
                    sx={{
                      minWidth: 72,
                      fontSize: '0.8rem',
                      py: 0.5,
                      px: 1.5,
                      '& .MuiButton-startIcon': { mr: 0.5 },
                    }}
                  >
                    Claim
                  </StyledButton>
                </span>
              </Tooltip>
            </ButtonContainer>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showPhonePopup} onClose={() => setShowPhonePopup(false)}>
        <DialogTitle>{businessName ? `${businessName} ` : 'Business phone'}</DialogTitle>
        <DialogContent>
          {phone ? (
            <>
              <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
                {formatPhoneIfNeeded(phone)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PhoneIcon />}
                  href={`tel:${phone.replace(/\D/g, '')}`}
                >
                  Call
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Phone number not available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPhonePopup(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLocationPopup} onClose={() => setShowLocationPopup(false)}>
        <DialogTitle>{businessName ? `${businessName} ` : 'Business address'}</DialogTitle>
        <DialogContent>
          {resolvedAddress ? (
            <>
              <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
                {resolvedAddress}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LocationOnIcon />}
                  onClick={handleDirectionsClick}
                >
                  Directions
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Address not available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLocationPopup(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showMapsChoice} onClose={() => setShowMapsChoice(false)}>
        <DialogTitle>Choose maps app</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
            Select your preferred maps application
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LocationOnIcon />}
              onClick={handleOpenGoogleMaps}
              sx={{ minWidth: 140 }}
            >
              Google Maps
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LocationOnIcon />}
              onClick={handleOpenAppleMaps}
              sx={{ minWidth: 140 }}
            >
              Apple Maps
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMapsChoice(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InfoPopup;
